'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  formatCvssReasonsError,
  getCvssVersionFromVector,
  isCvssBaseVectorChanged,
  validateCvssReasons,
  type CvssEnvironmentalRequirements,
  type CvssMetricReason,
  type CvssVersion,
} from '@terra/shared-cvss';
import { HeadingSection, MetaDefault } from '../../components/typography';
import { CvssCalculator } from '../CVSS/CvssCalculator';
import { CvssMetricReasonsFields } from '../CVSS/CvssMetricReasonsFields';
import type { EditableFields } from './finding-editable-fields';

export type FindingEditDrawerCvssSectionProps = {
  cvss: EditableFields['cvss'];
  originalCvss: EditableFields['cvss'];
  defaultCvssVersion?: CvssVersion;
  environmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
  onScoreChange: (score: string, vector: string, severity?: string) => void;
  onReasonsChange: (next: {
    metricReasons: CvssMetricReason[];
    explanation: string;
  }) => void;
};

export const getCvssReasonsSaveError = ({
  next,
  original,
}: {
  next: EditableFields['cvss'];
  original: EditableFields['cvss'];
}): string | null => {
  if (
    !next?.vector ||
    !isCvssBaseVectorChanged({ previous: original?.vector, next: next.vector })
  ) {
    return null;
  }

  const validation = validateCvssReasons({
    vector: next.vector,
    metricReasons: next.metricReasons ?? [],
    explanation: next.explanation,
    previousVector: original?.vector,
    previousReasons: original?.metricReasons ?? [],
  });

  return validation.ok
    ? null
    : formatCvssReasonsError(
        validation,
        getCvssVersionFromVector(next.vector),
      );
};

export function FindingEditDrawerCvssSection({
  cvss,
  originalCvss,
  defaultCvssVersion,
  environmentalRequirements,
  onScoreChange,
  onReasonsChange,
}: FindingEditDrawerCvssSectionProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.sections.cvssAssessment',
  );

  return (
    <VStack
      gap={3}
      p={4}
      bg="bg.default"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.default"
      w="full"
      alignItems="stretch"
    >
      <HStack w="full" justifyContent="space-between" alignItems="center">
        <HeadingSection>{t('title')}</HeadingSection>
        <MetaDefault color="fg.muted">{t('description')}</MetaDefault>
      </HStack>

      <CvssCalculator
        key={defaultCvssVersion ?? 'auto'}
        initialScore={cvss?.score || ''}
        initialVector={cvss?.vector || ''}
        initialEnvironmentalVector={cvss?.environmentalVector}
        version={defaultCvssVersion}
        findingVersion={
          getCvssVersionFromVector(cvss?.vector || '') ??
          (cvss?.version === '4.0' ||
          cvss?.version === '3.1' ||
          cvss?.version === '3.0'
            ? cvss.version
            : null)
        }
        environmentalRequirements={environmentalRequirements}
        onScoreChange={onScoreChange}
      />
      <CvssMetricReasonsFields
        originalCvss={originalCvss}
        cvss={cvss}
        onReasonsChange={onReasonsChange}
      />
    </VStack>
  );
}
